import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    CircularProgress,
    Typography,
    Alert,
    Button,
} from '@mui/material';
import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import snsWebSdk from '@sumsub/websdk';
import { logger } from '../../utils/logger';

interface KYCVerificationModalProps {
    open: boolean;
    accessToken: string;
    onClose: () => void;
    onComplete: () => void;
    onError: (error: string) => void;
    onTokenExpired: () => Promise<string>;
    errorMessage?: string;
    onRetry?: () => void;
}

const KYCVerificationModal: React.FC<KYCVerificationModalProps> = ({
    open,
    accessToken,
    onClose,
    onComplete,
    onError,
    onTokenExpired,
    errorMessage,
    onRetry,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const sdkInstanceRef = useRef<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [sdkError, setSdkError] = useState<string | null>(null);

    const launchSdk = useCallback(async () => {
        if (!containerRef.current || !accessToken) return;

        setIsLoading(true);
        setSdkError(null);

        try {
            const snsWebSdkInstance = snsWebSdk
                .init(accessToken, async () => {
                    // Token refresh callback
                    try {
                        const newToken = await onTokenExpired();
                        return newToken;
                    } catch (err: any) {
                        logger.error('Failed to refresh token:', err?.message);
                        throw err;
                    }
                })
                .withConf({
                    lang: 'en',
                    theme: 'dark',
                })
                .withOptions({
                    addViewportTag: false,
                    adaptIframeHeight: true,
                })
                .on('idCheck.onStepCompleted', (payload: any) => {
                    logger.debug('KYC step completed');
                })
                .on('idCheck.onApplicantStatusChanged', (payload: any) => {
                    logger.debug('KYC applicant status changed:', { reviewAnswer: payload?.reviewResult?.reviewAnswer });
                    if (payload.reviewResult?.reviewAnswer === 'GREEN') {
                        onComplete();
                    }
                })
                .on('idCheck.onApplicantLoaded', (payload: any) => {
                    logger.debug('KYC applicant loaded');
                    setIsLoading(false);
                })
                .on('idCheck.onError', (error: any) => {
                    logger.error('KYC SDK error:', error?.message);
                    setSdkError(error?.message || 'Verification error occurred');
                    onError(error?.message || 'Verification error occurred');
                })
                .build();

            sdkInstanceRef.current = snsWebSdkInstance;
            snsWebSdkInstance.launch('#sumsub-websdk-container');
        } catch (err: any) {
            logger.error('Failed to initialize Sumsub SDK:', err?.message);
            setSdkError(err?.message || 'Failed to initialize verification');
            setIsLoading(false);
        }
    }, [accessToken, onComplete, onError, onTokenExpired]);

    useEffect(() => {
        if (open && accessToken) {
            // Small delay to ensure container is mounted
            const timer = setTimeout(() => {
                launchSdk();
            }, 100);

            return () => {
                clearTimeout(timer);
                if (sdkInstanceRef.current) {
                    try {
                        sdkInstanceRef.current.destroy();
                    } catch (e) {
                        // Ignore destroy errors
                    }
                    sdkInstanceRef.current = null;
                }
            };
        }
    }, [open, accessToken, launchSdk]);

    const handleClose = () => {
        if (sdkInstanceRef.current) {
            try {
                sdkInstanceRef.current.destroy();
            } catch (e) {
                // Ignore destroy errors
            }
            sdkInstanceRef.current = null;
        }
        onClose();
    };

    const displayError = errorMessage || sdkError;

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                // Prevent closing by backdrop click or escape
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
                handleClose();
            }}
            maxWidth="md"
            fullWidth
            aria-labelledby="kyc-verification-modal-title"
            PaperProps={{
                sx: {
                    minHeight: '600px',
                    bgcolor: 'background.paper',
                },
            }}
        >
            <DialogTitle
                id="kyc-verification-modal-title"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                <Typography variant="h6" component="span">
                    Identity Verification
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, position: 'relative', minHeight: '500px' }}>
                {isLoading && !displayError && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'background.paper',
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress size={48} />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            Loading verification...
                        </Typography>
                    </Box>
                )}

                {displayError && (
                    <Box sx={{ p: 3 }}>
                        <Alert
                            severity="error"
                            action={
                                onRetry && (
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={onRetry}
                                        startIcon={<RefreshIcon />}
                                    >
                                        Retry
                                    </Button>
                                )
                            }
                        >
                            {displayError}
                        </Alert>
                    </Box>
                )}

                <Box
                    id="sumsub-websdk-container"
                    data-testid="sumsub-websdk-container"
                    ref={containerRef}
                    sx={{
                        width: '100%',
                        minHeight: '500px',
                        '& iframe': {
                            border: 'none',
                            width: '100%',
                            minHeight: '500px',
                        },
                    }}
                />
            </DialogContent>
        </Dialog>
    );
};

export default KYCVerificationModal;
